/**
 * "Triage is updating": a deploy stops the worker, which serves this dashboard and its API,
 * for the length of the on-box build (Galley #38663, the owner's #38648). Meanwhile the deploy's
 * stand-in answers every request 503 with `X-Triage-Updating: 1`. An open page notes that here,
 * shows `UpdatingBanner`, and catches up once Triage is back.
 *
 * "Back" means the database is attached, not just that something answered. At every boot the
 * dashboard listens about 22 s before its database pool is set; in that window `/health` and any
 * route that never touches the pool answer 200 while every other route answers 503
 * `DB_NOT_CONFIGURED` (Galley #38986, #39101). So only the banner's probe of `/api/ready` clears
 * the state (`noteReadyProbe`); an ordinary success through `api<T>` doesn't.
 *
 * Only that answer sets the state. Every other one is an ordinary error, exactly as before: a 503
 * without the header, a 500, a 404, and a 502 or 504 too. Triage's own routes answer 502 for a
 * database error (Galley #38775), so a bare 502 can't mean "updating". The load balancer's own 502
 * in the seconds around the stand-in shows today's error. Nothing here changes what `api<T>`
 * returns or throws.
 */

/** The header the deploy's stand-in sends with its 503 (`scripts/updating-page/`). */
export const UPDATING_HEADER = "X-Triage-Updating";

/** How often an open page asks `/api/ready` whether Triage is back, while it's updating. */
export const PROBE_INTERVAL_MS = 10_000;

/** The probe: 200 once the dashboard's database is attached, 503 `DB_NOT_CONFIGURED` before. */
export const READY_PATH = "/api/ready";

type Listener = () => void;

let updating = false;
const listeners = new Set<Listener>();

function set(next: boolean): void {
  if (next === updating) return;
  updating = next;
  for (const l of listeners) l();
}

/** True only for the deploy stand-in's answer: a 503 with `X-Triage-Updating: 1`. */
export function isUpdatingResponse(res: Pick<Response, "status" | "headers">): boolean {
  return res.status === 503 && res.headers.get(UPDATING_HEADER) === "1";
}

/**
 * Note one response from Triage's own server (`api<T>` calls this for every request). An updating
 * answer raises the state; nothing here clears it, because a 200 from a route that never touches
 * the database proves nothing during a boot (#39101). Every other answer leaves it as it was.
 */
export function noteResponse(res: Pick<Response, "status" | "headers">): void {
  if (isUpdatingResponse(res)) set(true);
}

/**
 * True while the probe's answer says Triage isn't serving yet: no answer at all (`null`), or a
 * 502, 503 or 504. That covers the stand-in, the load balancer around a restart, and the boot
 * window's `DB_NOT_CONFIGURED`. Anything else is the real server answering: a 200 (back), a 401 or
 * 403 (the session ran out during the deploy, so the page's own sign-in redirect takes over), an
 * older binary without `/api/ready` (its SPA fallback answers 200 `index.html`, or 404 without the
 * SPA directory), so a rollback can never pin the banner, and any other error.
 */
export function stillUpdating(status: number | null): boolean {
  return status === null || status === 502 || status === 503 || status === 504;
}

/** Note the banner's probe of `READY_PATH`: the only thing that clears the state. */
export function noteReadyProbe(res: Pick<Response, "status"> | null): void {
  if (!stillUpdating(res === null ? null : res.status)) set(false);
}

export function isUpdating(): boolean {
  return updating;
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Tests only: back to the starting state between cases. */
export function resetUpdatingForTests(): void {
  updating = false;
  listeners.clear();
}
